/**
 * Angle-driving noise for flow fields: Perlin, FBM, or domain-warped FBM.
 * Outputs ≈ [0, 1] like createSeededNoise for use as Noise01 in buildFlowGrid.
 */

import type { Noise01 } from "./flowField";
import { createSeededNoise } from "./perlinNoise";

export type FlowAngleNoiseMode = "perlin" | "fbm" | "domain_warp";

export const FLOW_ANGLE_NOISE_MODES: {
    value: FlowAngleNoiseMode;
    label: string;
}[] = [
    { value: "perlin", label: "Perlin" },
    { value: "fbm", label: "FBM (fractal)" },
    { value: "domain_warp", label: "Domain warp" },
];

export interface FlowAngleNoiseParams {
    mode: FlowAngleNoiseMode;
    /** FBM octaves for the angle sample (fbm and domain_warp). Ignored for perlin. */
    octaves: number;
    /** Warp displacement strength in noise-space units (domain_warp only). */
    warpStrength: number;
    /** Multiplier on (x, y) when sampling warp offsets (domain_warp only). */
    warpFrequency: number;
}

/**
 * Fractional Brownian motion: sum octaves of `noise` with doubling frequency
 * and halving amplitude. Stable when noise returns [0, 1].
 */
export function fbm01(
    noise: (x: number, y: number) => number,
    x: number,
    y: number,
    octaves: number,
): number {
    const o = Math.max(1, Math.min(8, Math.round(octaves)));
    let value = 0;
    let amplitude = 0.5;
    let frequency = 1;
    for (let i = 0; i < o; i++) {
        value += amplitude * noise(x * frequency, y * frequency);
        amplitude *= 0.5;
        frequency *= 2;
    }
    return value;
}

function clampOctaves(o: number): number {
    return Math.max(1, Math.min(8, Math.round(o)));
}

export function createFlowAngleNoise(
    seed: string,
    params: FlowAngleNoiseParams,
): Noise01 {
    const octaves = clampOctaves(params.octaves);
    const base = seed || "default";

    if (params.mode === "perlin") {
        const n = createSeededNoise(base);
        return (x, y) => n(x, y);
    }

    if (params.mode === "fbm") {
        const n = createSeededNoise(base);
        return (x, y) => fbm01(n, x, y, octaves);
    }

    const nV = createSeededNoise(`${base}|angle`);
    const nWx = createSeededNoise(`${base}|warpx`);
    const nWy = createSeededNoise(`${base}|warpy`);
    const wS = Math.max(0, params.warpStrength);
    const wF = Math.max(0.05, params.warpFrequency);

    return (x, y) => {
        const wx = nWx(x * wF, y * wF) - 0.5;
        const wy = nWy(x * wF + 13.1, y * wF + 7.9) - 0.5;
        return fbm01(nV, x + wx * wS, y + wy * wS, octaves);
    };
}
